import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { HttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { createPersistedQueryLink } from 'apollo-link-persisted-queries';
import { onError } from 'apollo-link-error';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher
} from 'apollo-cache-inmemory';
import { getAuthTokens } from 'util/auth';
import { getSurrogateKey } from 'util/cache';
import introspectionQueryResultData from '../graphQLFragmentTypes.json';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData
});

const isBrowser = typeof window !== 'undefined';

/* eslint-disable prefer-destructuring */
const GRAPHQL_URI = process.env.GRAPHQL_URI;
// convenience for devs if env is not updated to include GRAPHQL_SSR_URI
const GRAPHQL_SSR_URI = process.env.GRAPHQL_SSR_URI || GRAPHQL_URI;
/* eslint-enable prefer-destructuring */
const graphqlUri = isBrowser ? GRAPHQL_URI : GRAPHQL_SSR_URI;

let apolloClient = null;

// Polyfill fetch() on the server (used by apollo-client)
if (!isBrowser) {
  global.fetch = fetch;
}

const LINK_TIMEOUT_MS = 20000; // 20 second timeout

const CACHED_QUERIES = [
  'page',
  'fetchMoreContent',
  // see pages/image/*
  'prismImage',
  'prismColor',
  'prismTag',
  // gift guide 2019
  'filteredProductListings',
  // Small Cool
  'formSubmissions'
];

function create(initialState, { headers = {}, onErrorHandler } = {}) {
  const cacheConfigLink = setContext(request => {
    const { variables, operationName } = request;

    // do not cache queries during SSR or if they are not in the list
    if (!isBrowser || !CACHED_QUERIES.includes(operationName)) {
      return {
        headers: {
          // using a custom cache-control header here because standard headers
          // like Cache-Control: no-cache are abused and we have to ignore them
          // on Fastly.
          'no-cache': 'true'
        }
      };
    }

    // the goal here is to base the GQL query's cache key off the page's
    // surrogate key, thereby binding the page and its data query together so we
    // can easily purge both together. we also add a generic query key ('fegql')
    // that allows us to purge over all GQL queries at once.
    //
    // NOTE: this assumes cacheable queries have a 'path' variable. since we
    // want to cache prism queries, they have been modified to include a 'path'
    // variable even though they don't technically need it.
    //
    // CLEANUP: devise a way to access the current page's path in this context
    // irrespective of the 'path' query variable. or is this approach not so
    // bad?
    const pageKey = getSurrogateKey(variables.path || '');
    const cacheKey = `fegql query:${operationName} ${pageKey}`.trim();

    // set headers for cached queries
    return {
      headers: {
        // we don't want to cache queries for "preview" pages
        ...(variables.preview
          ? {
              // using a custom cache-control header here because standard
              // headers like Cache-Control: no-cache are abused and we have to
              // ignore them on Fastly.
              'no-cache': variables.preview
            }
          : undefined),

        // provide fastly the desired surrogate key for the graphql response by
        // sending a "cache-key" http header with the graphql REQUEST. our
        // fastly service is configured to look for this header and then set the
        // surrogate key in the RESPONSE header to this value. (see vcl_fetch)
        'cache-key': cacheKey
      }
    };
  });

  const authLink = setContext((_, { headers: existingHeaders }) => {
    const { idToken, accessToken } = getAuthTokens();
    return {
      headers: {
        ...existingHeaders,
        authorization: idToken ? `Bearer ${idToken}` : '',
        'access-token': accessToken || ''
      }
    };
  });

  const errorLink = onError(e => {
    if (onErrorHandler) {
      onErrorHandler(e);
    }
  });

  const persistedLink = createPersistedQueryLink({
    // Use GETs everywhere to allow better visibility into HTTP requests
    useGETForHashedQueries: true
  });

  const timeoutLink = new ApolloLinkTimeout(LINK_TIMEOUT_MS);

  const httpLink = new HttpLink({
    uri: graphqlUri,
    credentials: 'same-origin',
    headers
  });

  const link = ApolloLink.from([
    cacheConfigLink,
    errorLink,
    persistedLink,
    timeoutLink,
    authLink,
    httpLink
  ]);

  const inMemoryCache = new InMemoryCache({
    dataIdFromObject: object => {
      if (object.__typename === null) {
        return null;
      }
      if (object.ndbId) {
        return `${object.__typename}_${object.ndbId}`;
      }
      if (object.id) {
        return `${object.__typename}_${object.id}`;
      }
      return null;
    },
    fragmentMatcher
  }).restore(initialState || {});

  return new ApolloClient({
    connectToDevTools: isBrowser,
    ssrMode: !isBrowser, // Disables forceFetch on the server (so queries are only run once)
    link,
    cache: inMemoryCache
  });
}

export default function initApollo(initialState, ops) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!isBrowser) {
    return create(initialState, ops);
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = create(initialState, ops);
  }

  return apolloClient;
}
